import { create } from 'zustand';

/**
 * Zustand store for Ask Swirl chat interface state
 * Manages chips, modals, and selector state
 */
export const useAskSwirlStore = create((set, get) => ({
  // Chips state
  chips: [],
  
  // Prefill input text
  prefilledInput: '',
  
  // Modal states
  isSelectorOpen: false,
  isUploadModalOpen: false,
  
  // Selector state
  selectorTab: 'items', // 'items' | 'collections' | 'focus' | 'tags' | 'recent'
  selectorSelected: [],
  
  // Focus context - for linking chats to Focus items
  focusId: null,
  focusCardId: null,
  
  // Actions
  addChip: (chip) => {
    const { chips } = get();
    // Prevent duplicates
    if (!chips.find(c => c.id === chip.id)) {
      console.log('➕ [askSwirlStore] Adding chip:', chip.id, chip.label, 'Stack trace:', new Error().stack.split('\n')[2]);
      // Add timestamp to track when chip was added (most recent first)
      const chipWithTimestamp = {
        ...chip,
        addedAt: Date.now(), // Track when chip was added
      };
      // Add new chip and sort by most recent first
      const updatedChips = [...chips, chipWithTimestamp].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
      set({ chips: updatedChips });
      console.log('✅ [askSwirlStore] Chip added. Total chips now:', updatedChips.length);
    } else {
      console.log('⚠️ [askSwirlStore] Duplicate chip prevented:', chip.id);
    }
  },
  
  removeChip: (chipId) => {
    const { chips } = get();
    set({ chips: chips.filter(c => c.id !== chipId) });
  },
  
  clearChips: () => {
    const currentChips = get().chips;
    console.log('🧹 [askSwirlStore] clearChips called - clearing all chips. Current count:', currentChips.length, 'IDs:', currentChips.map(c => c.id));
    // CRITICAL: Force immediate synchronous state update with new array reference
    // Use direct setState to bypass any potential issues
    set({ chips: [] }, false);
    // Verify immediately
    const afterChips = get().chips;
    if (afterChips.length > 0) {
      console.warn('⚠️ [askSwirlStore] Chips still exist after clear, forcing again...', afterChips.length, afterChips.map(c => c.id));
      // Force clear again with new array reference
      set({ chips: [] }, false);
      // Final check
      const finalCheck = get().chips;
      if (finalCheck.length > 0) {
        console.error('❌ [askSwirlStore] CRITICAL: Chips still exist after multiple clear attempts!', finalCheck);
      }
    }
    // Final verification
    const finalChips = get().chips;
    console.log('✅ [askSwirlStore] Chips cleared. Final count:', finalChips.length);
    return finalChips.length === 0;
  },
  
  setPrefilledInput: (text) => {
    set({ prefilledInput: text });
  },
  
  clearPrefilledInput: () => {
    set({ prefilledInput: '' });
  },
  
  openSelector: () => {
    set({ isSelectorOpen: true, selectorSelected: [] });
  },
  
  closeSelector: () => {
    set({ isSelectorOpen: false, selectorSelected: [] });
  },
  
  openUploadModal: () => {
    set({ isUploadModalOpen: true });
  },
  
  closeUploadModal: () => {
    set({ isUploadModalOpen: false });
  },
  
  setSelectorTab: (tab) => {
    set({ selectorTab: tab, selectorSelected: [] });
  },
  
  toggleSelectorItem: (item) => {
    const { selectorSelected } = get();
    const exists = selectorSelected.find(i => i.id === item.id);
    if (exists) {
      set({ selectorSelected: selectorSelected.filter(i => i.id !== item.id) });
    } else {
      set({ selectorSelected: [...selectorSelected, item] });
    }
  },
  
  confirmSelectorSelection: () => {
    const { selectorSelected, chips } = get();
    // Convert selected items to chips and add them with full file information
    const newChips = selectorSelected.map(item => ({
      id: item.id,
      type: item.type || 'item',
      label: item.label || item.title || item.name || 'Untitled',
      thumbnail: item.thumbnail || item.thumbnail_url || item.image_url,
      // Store full file information for attachments
      file_url: item.file_url || item.url || null,
      file_name: item.file_name || item.title || item.label || 'file',
      file_type: item.file_type || 'unknown',
      file_size: item.file_size || null,
      source: 'swirlspace', // Track that this came from Swirlspace selection
    }));
    
    // Add all new chips (duplicates will be prevented by addChip)
    newChips.forEach(chip => get().addChip(chip));
    
    // Close selector and clear selection
    set({ isSelectorOpen: false, selectorSelected: [] });
  },
  
  // Focus context actions
  setFocusContext: (focusId, focusCardId = null) => {
    console.log('🎯 [askSwirlStore] Setting Focus context:', { focusId, focusCardId });
    set({ focusId, focusCardId });
  },
  
  clearFocusContext: () => {
    console.log('🎯 [askSwirlStore] Clearing Focus context');
    set({ focusId: null, focusCardId: null });
  },
}));

